import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Vote } from "lucide-react";
import { Badge, Button, Card, Field } from "./ui";
import { api, type PollDto } from "../lib/api";

// ONE form for every open poll: students pick all their options here, enter a
// single voting code, and submit everything with one button. The backend lands
// every ballot in one transaction (all-or-nothing).

interface Props {
  polls: PollDto[];
  onSubmitted: () => void;
}

export default function CombinedBallotForm({ polls, onSubmitted }: Props) {
  const [selections, setSelections] = useState<Record<number, number[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const toggle = (poll: PollDto, optionId: number) => {
    setError(null);
    setSuccess(null);
    setSelections((prev) => {
      const current = prev[poll.id] ?? [];
      const has = current.includes(optionId);
      let next: number[];
      if (has) {
        next = current.filter((id) => id !== optionId);
      } else if (poll.maxChoices <= 1) {
        next = [optionId];
      } else {
        next = [...current, optionId].slice(0, poll.maxChoices);
      }
      return { ...prev, [poll.id]: next };
    });
  };

  const answered = polls.filter((p) => (selections[p.id]?.length ?? 0) > 0).length;
  const allAnswered = polls.length > 0 && answered === polls.length;

  const submit = async () => {
    setError(null);
    setSuccess(null);
    const ballots = polls
      .filter((p) => (selections[p.id]?.length ?? 0) > 0)
      .map((p) => ({ pollId: p.id, optionIds: selections[p.id] }));
    if (ballots.length === 0) {
      setError("Select at least one option before submitting.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.submitStudentCombined(ballots);
      setSuccess(
        res.message ??
          `Thank you! Your votes were recorded for ${ballots.length} poll${ballots.length === 1 ? "" : "s"}.`,
      );
      onSubmitted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "We could not save your votes. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-white">Combined ballot form</h3>
          <p className="mt-1 max-w-2xl text-sm text-white/50">
            Select your options in every poll below, enter your voting code once, then submit all
            ballots together. One code = one ballot per poll.
          </p>
        </div>
        <Badge tone={allAnswered ? "success" : "neutral"}>
          {answered} of {polls.length} polls answered
        </Badge>
      </div>

      <div className="mt-6 space-y-8">
        {polls.map((poll, idx) => (
          <fieldset key={poll.id} className="rounded-xl border border-white/10 p-5">
            <legend className="px-2 text-sm font-medium uppercase tracking-[0.14em] text-wandor-prompt">
              {String(idx + 1).padStart(2, "0")} · {poll.category}
            </legend>
            <p className="text-lg font-medium text-white">{poll.title}</p>
            {poll.description && <p className="mt-1 text-sm text-white/50">{poll.description}</p>}
            <div className="mt-1 text-xs text-white/40">
              {poll.maxChoices > 1 ? `Pick up to ${poll.maxChoices}` : "Pick one"}
              {poll.closesAt ? " · deadline applies" : ""}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-2.5 md:grid-cols-2">
              {poll.options
                .filter((o) => o.published)
                .map((opt) => {
                  const selected = (selections[poll.id] ?? []).includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggle(poll, opt.id)}
                      aria-pressed={selected}
                      className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                        selected
                          ? "border-wandor-prompt bg-wandor-prompt/15 text-white"
                          : "border-white/15 text-white/80 hover:border-white/35 hover:text-white"
                      }`}
                    >
                      <span className="block text-[15px] font-medium">{opt.label}</span>
                      {opt.description && (
                        <span className="mt-0.5 block text-xs text-white/50">{opt.description}</span>
                      )}
                    </button>
                  );
                })}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-white/15 p-5">
        <Field label="Ballot submission" htmlFor="combined-submit">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button onClick={() => void submit()} disabled={busy || polls.length === 0}>
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Vote className="h-4 w-4" aria-hidden="true" />
            )}
            {busy ? "Submitting all ballots…" : "Submit all ballots"}
          </Button>
          </div>
        </Field>
        {!allAnswered && polls.length > 0 && (
          <p className="mt-2 text-xs text-white/45">
            You can submit what you have answered — unanswered polls are skipped. You may revise your
            ballots while each poll is open (where revisions are allowed).
          </p>
        )}
        {error && (
          <p className="mt-3 flex items-start gap-2 text-sm text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
        {success && (
          <p className="mt-3 flex items-start gap-2 text-sm text-emerald-300">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {success}
          </p>
        )}
      </div>
    </Card>
  );
}
